import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, ArrowRight, CheckCircle2, Save, HeartHandshake } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFormDraft } from "../hooks/useFormDraft";

/**
 * Pré-cadastro público de profissional credenciado.
 *
 * Submete uma CANDIDATURA (POST /api/applications, type="profissional") — não
 * cria conta nem loga. O admin RAF aprova (até 48h) e o profissional recebe as
 * credenciais + termos comerciais completos dentro da área autenticada.
 * Rascunho é auto-salvo em localStorage (useFormDraft) — não perde se fechar aba.
 *
 * Feb 2026 — Unificação FP: mesmo componente atende `/seja-profissional`
 * (origin=raf) e `/filho-presente/cadastre-profissional` (origin=fp). O prop
 * `origin="fp"` muda o branding (âmbar) e coleta campos extras (categoria FP,
 * conselho, cidade, bio, modalidades) que o admin usa no aprovar pra popular o
 * doc `users` já com `serves_fp=True` + trial de boas-vindas Prata 3m.
 */
const ProfessionalApply = ({ origin = "raf" }) => {
  const isFp = origin === "fp";
  const themeButton = isFp
    ? "bg-amber-500 hover:bg-amber-600"
    : "bg-emerald-700 hover:bg-emerald-800";
  const focusRing = isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500";
  const eyebrow = isFp
    ? "Credenciamento profissional — Filho Presente"
    : "Credenciamento profissional";
  const heroTitle = isFp
    ? <>Torne-se um <span className="text-amber-600">Profissional do Filho Presente</span></>
    : <>Torne-se um <span className="text-emerald-700">Profissional RAF Direct</span></>;

  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    // Campos FP extras — só populam quando origin="fp"
    display_name: "", category: "", specialty: "",
    council_type: "", council_number: "", council_state: "",
    city: "", state: "", bio: "",
    modalities: isFp ? ["online"] : [],
    session_value: "",
  });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { clear } = useFormDraft(
    isFp ? "fp.professional-apply.draft" : "raf.professional-apply.draft",
    form, setForm,
  );

  useEffect(() => {
    if (isFp) {
      api.get("/fp/professionals/categories")
        .then((r) => setCats(r.data.professional_categories || []))
        .catch(() => setCats([]));
    }
  }, [isFp]);

  const toggleMod = (m) => setForm((f) => ({
    ...f, modalities: f.modalities.includes(m) ? f.modalities.filter((x) => x !== m) : [...f.modalities, m],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        type: "profissional",
        origin,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message || "",
      };
      if (isFp) {
        payload.professional_data = {
          display_name: form.display_name || form.name,
          category: form.category,
          specialty: form.specialty,
          council_type: form.council_type,
          council_number: form.council_number,
          council_state: form.council_state,
          city: form.city,
          state: form.state,
          bio: form.bio,
          modalities: form.modalities,
          session_value: form.session_value ? Number(form.session_value) : null,
        };
      }
      await api.post("/applications", payload);
      clear();
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar candidatura");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="professional-apply-success">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${isFp ? "bg-amber-100" : "bg-emerald-100"}`}>
          <CheckCircle2 className={`w-9 h-9 ${isFp ? "text-amber-700" : "text-emerald-700"}`} />
        </div>
        <h1 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight">
          Candidatura enviada com sucesso
        </h1>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Recebemos seu pré-cadastro. Nossa equipe {isFp ? "Filho Presente" : "RAF Direct"} analisará seu perfil em
          até <b>48 horas úteis</b>. Se aprovado, você receberá por e-mail e WhatsApp as
          credenciais de acesso, os termos comerciais completos e o próximo passo (perfil,
          documentos e agenda).
          {isFp && <> Você já nasce com <b>trial Prata de 3 meses</b> pra aparecer na vitrine desde o dia 1.</>}
        </p>
        <p className="text-slate-500 text-sm mt-4">
          Nenhuma conta foi criada ainda — o acesso é liberado só após aprovação (até 48h úteis).
        </p>
        <Link to={isFp ? "/filho-presente" : "/"} className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid="professional-apply-back">
          Voltar para a home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="professional-apply-page">
      <div className={`text-xs uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1.5 ${isFp ? "text-amber-600" : "text-emerald-700"}`}>
        {isFp ? <HeartHandshake className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />} {eyebrow}
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight mt-2">
        {heroTitle}
      </h1>
      <p className="text-slate-600 mt-4 leading-relaxed text-left">
        {isFp
          ? <>O <b>Filho Presente</b> conecta profissionais da saúde do idoso — geriatras, gerontólogos, fisioterapeutas geriátricos, enfermeiros home care, nutricionistas, terapeutas, cuidadores. Nossa equipe faz a triagem e encaminha os casos compatíveis com seu perfil pra sua agenda.</>
          : <>Atenda famílias em busca de saúde mental, física e social — psiquiatras, clínicos, psicólogos, terapeutas, nutricionistas, enfermeiros, assistentes sociais, educadores físicos e afins. A RAF Direct <b>faz a triagem inteligente</b> e os casos compatíveis com seu perfil profissional serão <b>encaminhados para compor sua agenda</b>.</>}
      </p>
      <p className="text-slate-500 text-sm mt-2 text-left">
        Envie sua candidatura abaixo. Após análise (até 48h úteis), você recebe as credenciais e conclui o cadastro (perfil, documentos, agenda) na área autenticada. Mesmo padrão do cadastro RAF Direct — mesmos campos, mesmos critérios.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="professional-apply-form">
        <div className="flex justify-end -mb-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1" title="Seus dados são salvos automaticamente neste navegador enquanto você preenche.">
            <Save className="w-3 h-3" /> rascunho salvo
          </span>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nome completo *</label>
          <input required minLength={3} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="pa-name" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">E-mail profissional *</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="pa-email" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Telefone / WhatsApp *</label>
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="pa-phone" placeholder="(11) 99999-9999"
              className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
          </div>
        </div>

        {/* Campos extras exclusivos FP — categoria, conselho, cidade, bio, modalidades */}
        {isFp && (
          <>
            <div className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100 pb-2 pt-3">Perfil profissional (Filho Presente)</div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nome público (como aparece na vitrine)</label>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="pa-display-name" placeholder="Ex.: Dra. Camila F."
                className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Categoria *</label>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="pa-category" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`}>
                  <option value="">Selecione...</option>
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Especialidade</label>
                <input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                  data-testid="pa-specialty" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Conselho</label>
                <select value={form.council_type} onChange={(e) => setForm({ ...form, council_type: e.target.value })}
                  data-testid="pa-council-type" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`}>
                  <option value="">—</option>
                  {["CRM","CRP","COREN","CRN","CREFITO","CFFa","CREF","Outros"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Número</label>
                <input value={form.council_number} onChange={(e) => setForm({ ...form, council_number: e.target.value })}
                  data-testid="pa-council-number" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">UF conselho</label>
                <input maxLength={2} value={form.council_state} onChange={(e) => setForm({ ...form, council_state: e.target.value.toUpperCase() })}
                  data-testid="pa-council-state" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Cidade</label>
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="pa-city" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Estado (UF)</label>
                <input maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                  data-testid="pa-state" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Sobre você (bio pública)</label>
              <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                data-testid="pa-bio" placeholder="Conte um pouco da sua experiência e abordagem..."
                className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Modalidades de atendimento</label>
              <div className="flex gap-2 flex-wrap mt-2">
                {["presencial", "online", "domiciliar"].map((m) => (
                  <button key={m} type="button" onClick={() => toggleMod(m)}
                    data-testid={`pa-mod-${m}`}
                    className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${
                      form.modalities.includes(m) ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white border-slate-300 text-slate-700"
                    }`}>{m}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Valor da consulta (R$)</label>
              <input type="number" min="0" step="0.01" value={form.session_value} onChange={(e) => setForm({ ...form, session_value: e.target.value })}
                data-testid="pa-session-value" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
            </div>
          </>
        )}

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {isFp ? "Mensagem para o admin (opcional)" : "Área de atuação e experiência (opcional)"}
          </label>
          <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="pa-message" placeholder={isFp ? "Alguma observação para a equipe?" : "Especialidade, conselho de classe, anos de experiência, público-alvo..."}
            className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${focusRing}`} />
        </div>
        <button type="submit" disabled={loading}
          data-testid="pa-submit"
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-bold text-sm disabled:opacity-50 ${themeButton}`}>
          {loading ? "Enviando…" : <>Enviar candidatura <ArrowRight className="w-4 h-4" /></>}
        </button>
        <p className="text-xs text-slate-500">
          O envio deste formulário <b>não cria conta</b>. Você só recebe acesso ao painel após aprovação (até 48h úteis).
          {isFp && <> Ao aprovar, você entra automaticamente com <b>trial Prata 3 meses</b> — pra vitrine já ter seu selo desde o primeiro dia.</>}
        </p>
      </form>
    </div>
  );
};

export default ProfessionalApply;
